import { FC, useState } from "react";
import { Box, Button, Tooltip } from "@mui/material";

import { RiDeleteBinLine, RiLinkM } from "react-icons/ri";

import { Claim, EdiObject } from "../../../../../types/claims";
import { formatInstant } from "../../../../helpers/dataHelper";
import ClaimDocumentCard from "./ClaimDocumentCard";
import {
  describeManualPayment,
  getManualPaymentFields,
} from "./manualPayments";
import RemovePaymentDialog from "../RemovePaymentDialog";
import JoinPaymentDialog from "../join-payment/JoinPaymentDialog";
import { getJoinCandidates } from "../join-payment/joinPayments";
import InfoItem, { InfoSectionLabel } from "../InfoItem";

/**
 * Read-only view of a manual payment: what the letter said (fields it didn't
 * show are left out), who recorded it and when, and its correspondence to view
 * or download. Admins can join it with the electronic remit that duplicates it,
 * or remove it, from here.
 */
const ManualPaymentDetail: FC<{
  ediObj: EdiObject;
  claim: Claim;
  /** The claim's EDI objects, where the remits to join come from. */
  ediObjects: EdiObject[];
  /** Admins only: join and remove. */
  canManage: boolean;
  onRemoved: () => void;
  onJoined: () => void;
}> = ({ ediObj, claim, ediObjects, canManage, onRemoved, onJoined }) => {
  const [isRemoveOpen, setIsRemoveOpen] = useState(false);
  const [isJoinOpen, setIsJoinOpen] = useState(false);

  const fields = getManualPaymentFields(ediObj);
  const { manual, attachmentId } = ediObj;
  const document = manual?.document ?? null;
  const hasRemitToJoin = getJoinCandidates(ediObj, ediObjects).some(
    (candidate) => !candidate.isJoined,
  );

  return (
    <Box sx={{ p: 2, backgroundColor: "white" }}>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(100px, 1fr))",
          gap: 1.5,
        }}
      >
        {describeManualPayment(fields).map((row) => (
          <InfoItem
            key={row.label}
            label={row.label}
            valueColor={row.label === "Amount" ? "success.main" : undefined}
          >
            {row.value}
          </InfoItem>
        ))}
        {manual && (
          <InfoItem label="Recorded">
            {formatInstant(manual.createdAt)}
            {manual.createdBy ? ` · by ${manual.createdBy}` : ""}
          </InfoItem>
        )}
      </Box>

      {document && (
        <Box sx={{ pt: 2 }}>
          <InfoSectionLabel>Correspondence</InfoSectionLabel>
          <ClaimDocumentCard document={document} claimId={claim.id} />
        </Box>
      )}

      {canManage && attachmentId && (
        <>
          <Box sx={{ pt: 2, display: "flex", justifyContent: "flex-end", gap: 1 }}>
            <Tooltip
              title={
                hasRemitToJoin
                  ? ""
                  : "No electronic remit from the same payer level to join"
              }
            >
              <span>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<RiLinkM size={14} />}
                  disabled={!hasRemitToJoin}
                  onClick={() => setIsJoinOpen(true)}
                >
                  Join with remit…
                </Button>
              </span>
            </Tooltip>
            <Button
              variant="outlined"
              color="error"
              size="small"
              startIcon={<RiDeleteBinLine size={14} />}
              onClick={() => setIsRemoveOpen(true)}
            >
              Remove payment
            </Button>
          </Box>
          <RemovePaymentDialog
            open={isRemoveOpen}
            claim={claim}
            attachmentId={attachmentId}
            payment={fields}
            onClose={() => setIsRemoveOpen(false)}
            onRemoved={onRemoved}
          />
          <JoinPaymentDialog
            open={isJoinOpen}
            claim={claim}
            manual={ediObj}
            ediObjects={ediObjects}
            onClose={() => setIsJoinOpen(false)}
            onJoined={onJoined}
          />
        </>
      )}
    </Box>
  );
};

export default ManualPaymentDetail;
